import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

/** true quando o conteúdo está dentro do invólucro padrão com padding (sem `noWrapper`). */
const DialogScrollContext = React.createContext(false);

/* ── Overlay — mais suave com backdrop-blur ─────────────────────────────── */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in  data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/* ── Content ────────────────────────────────────────────────────────────── */
/** sm: confirmação · md: form curto · lg: form em 2–3 colunas · xl: conteúdo + propriedades */
const DIALOG_SIZES = {
  sm: 'max-w-[440px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[800px]',
  xl: 'max-w-[1040px]',
} as const;

const EDITABLE_SELECTOR =
  'input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable="true"]';

/** Formulário de verdade: texto longo ou 2+ campos. Um campo só (ex.: busca) ainda fecha com clique fora. */
function looksLikeForm(el: HTMLElement | null) {
  if (!el) return false;
  return Boolean(el.querySelector('textarea, [contenteditable="true"]')) || el.querySelectorAll(EDITABLE_SELECTOR).length >= 2;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    noWrapper?: boolean;
    /** @default true */
    showCloseButton?: boolean;
    overlayClassName?: string;
    size?: keyof typeof DIALOG_SIZES;
    /** Envio em andamento: Esc, clique fora e ✕ não fecham. */
    busy?: boolean;
  }
>(
  (
    {
      className,
      children,
      noWrapper,
      showCloseButton = true,
      overlayClassName,
      size,
      busy = false,
      onEscapeKeyDown,
      onInteractOutside,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<React.ElementRef<typeof DialogPrimitive.Content>>(null);
    React.useImperativeHandle(ref, () => innerRef.current!);

    return (
      <DialogPortal>
        {/* O overlay é a área que rola: modal centralizado; se passar da altura da tela, desce e a página rola. */}
        <DialogOverlay
          className={cn(
            'flex overflow-y-auto overscroll-contain px-4 py-8 max-sm:px-0 max-sm:pb-0',
            overlayClassName,
          )}
        >
          <DialogPrimitive.Content
            ref={innerRef}
            onEscapeKeyDown={(e) => {
              onEscapeKeyDown?.(e);
              if (busy) e.preventDefault();
            }}
            onInteractOutside={(e) => {
              onInteractOutside?.(e);
              if (e.defaultPrevented) return;
              // Clique na barra de rolagem do overlay não é "clique fora"
              const overlay = innerRef.current?.parentElement;
              const pointer = e.detail.originalEvent as PointerEvent;
              if (overlay && pointer.target === overlay && pointer.clientX >= overlay.clientWidth) {
                e.preventDefault();
                return;
              }
              // Clique fora não fecha formulários (perderia o que foi digitado); Esc, ✕ e Cancelar continuam.
              if (busy || looksLikeForm(innerRef.current)) e.preventDefault();
            }}
            className={cn(
              'relative m-auto w-full max-w-lg flex flex-col overflow-hidden rounded-2xl bg-background border border-border/50 shadow-modal',
              'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4',
              size && DIALOG_SIZES[size],
              className,
              // Celular: folha que sobe de baixo, largura total (vem por último para vencer larguras fixas)
              'max-sm:mb-0 max-sm:mt-auto max-sm:max-w-none max-sm:rounded-b-none',
            )}
            {...props}
          >
            {noWrapper ? (
              children
            ) : (
              <DialogScrollContext.Provider value={true}>
                <div className="p-6">{children}</div>

                {showCloseButton ? (
                  <DialogPrimitive.Close
                    disabled={busy}
                    className="absolute right-4 top-4 z-20 rounded-full p-1.5 text-muted-foreground opacity-70 ring-offset-background transition-all duration-150 hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-30"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Fechar</span>
                  </DialogPrimitive.Close>
                ) : null}
              </DialogScrollContext.Provider>
            )}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/* ── Header ─────────────────────────────────────────────────────────────── */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const inWrapper = React.useContext(DialogScrollContext);
  return (
    <div
      className={cn('flex flex-col space-y-1.5 pr-8', inWrapper && 'pb-3', className)}
      {...props}
    />
  );
};
DialogHeader.displayName = 'DialogHeader';

/* ── Footer — com border-top, encostado nas bordas do modal ─────────────── */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const inScroll = React.useContext(DialogScrollContext);
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        'border-t border-border/60 bg-muted/30 px-6 py-4',
        inScroll && '-mx-6 -mb-6 mt-6 bg-background',
        className,
      )}
      {...props}
    />
  );
};
DialogFooter.displayName = 'DialogFooter';

/* ── Title ──────────────────────────────────────────────────────────────── */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-semibold leading-tight tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/* ── Description ────────────────────────────────────────────────────────── */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground leading-relaxed', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
